import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { QuotaPolicy, QuotaBand, ExclusionCategory } from '@cca/xero-shared';

// Editor for a QuotaPolicy. Controlled — the parent owns the policy object.
// Rates and percentages are shown to the registrar as percentages (7.5) but
// stored as fractions (0.075); the conversions live here so the rest of the
// app only ever handles the canonical fraction form.

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text2)',
  display: 'block',
  marginBottom: 2,
};
const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
};

function num(v: string): number {
  const n = Number(v.replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Percentages are shown to at most 1 decimal place everywhere. */
function pct1(x: number): number {
  return Math.round(x * 10) / 10;
}

/** A percentage input. Stores a FRACTION (0.075) but the registrar types a
 *  percent (7.5). While focused it holds the raw typed text — so typing isn't
 *  fought by reformatting (and `0.07 * 100`'s float noise never shows); on blur
 *  it displays the value to 1 dp. `value`/`onChange` are null when `allowNull`. */
function PctInput({
  value,
  onChange,
  width = 80,
  allowNull = false,
  id,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  width?: number;
  allowNull?: boolean;
  id?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const display = value == null ? '' : String(pct1(value * 100));
  return (
    <input
      id={id}
      className="input"
      type="text"
      inputMode="decimal"
      value={focused ? draft : display}
      placeholder={allowNull ? 'off' : undefined}
      style={{ width, textAlign: 'right' }}
      onFocus={() => {
        setDraft(display);
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw.trim() === '') return onChange(allowNull ? null : 0);
        const n = Number(raw.replace(/,/g, ''));
        if (Number.isFinite(n)) onChange(n / 100);
      }}
    />
  );
}

function NumField({
  label,
  value,
  onChange,
  width = 110,
  step,
  placeholder,
}: {
  label: string;
  value: number | '';
  onChange: (v: number) => void;
  width?: number;
  step?: string;
  placeholder?: string;
}) {
  return (
    <label style={{ display: 'inline-block', marginRight: 16, verticalAlign: 'top' }}>
      <span style={labelStyle}>{label}</span>
      <input
        className="input"
        type="number"
        step={step}
        value={value}
        placeholder={placeholder}
        style={{ width, textAlign: 'right' }}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    </label>
  );
}

/** A field that is either off (null) or a number — for the optional $ caps. */
function NullableNumField({
  label,
  value,
  onChange,
  width = 110,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  width?: number;
}) {
  return (
    <label style={{ display: 'inline-block', marginRight: 16, verticalAlign: 'top' }}>
      <span style={labelStyle}>{label}</span>
      <input
        className="input"
        type="number"
        value={value == null ? '' : value}
        placeholder="off"
        style={{ width, textAlign: 'right' }}
        onChange={(e) => {
          if (e.target.value === '') return onChange(null);
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          onChange(n);
        }}
      />
    </label>
  );
}

/** A slide on/off toggle, styled to sit beside the number fields. */
function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        width: 42,
        height: 24,
        flexShrink: 0,
        borderRadius: 999,
        border: '1px solid var(--border)',
        background: checked ? '#2f6f4f' : 'var(--bg2, #e8e3d8)',
        position: 'relative',
        cursor: 'pointer',
        padding: 0,
        transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 1,
          left: checked ? 20 : 1,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}

export function PolicyForm({
  policy,
  onChange,
  categories,
  versionLabel = 'Policy version',
  versionHint,
  hideVersion = false,
}: {
  policy: QuotaPolicy;
  onChange: (p: QuotaPolicy) => void;
  categories: ExclusionCategory[];
  versionLabel?: string;
  versionHint?: string;
  /** Hide the free-text policy-version input (Invoice Year is kept). Versions are
   *  auto-assigned on save now, so the registrar never types one. */
  hideVersion?: boolean;
}) {
  const set = (patch: Partial<QuotaPolicy>) => onChange({ ...policy, ...patch });
  const exclPct = policy.category_exclusion ?? {};
  const setCategoryPct = (key: string, pct: number) => {
    // Keep only positive fractions; drop a zeroed category by rebuilding the map.
    const next = Object.fromEntries(Object.entries(exclPct).filter(([k]) => k !== key));
    if (pct > 0) next[key] = pct / 100;
    set({ category_exclusion: next });
  };

  const setBand = (i: number, patch: Partial<QuotaBand>) => {
    const bands = policy.bands.map((b, j) => (j === i ? { ...b, ...patch } : b));
    set({ bands });
  };
  const addBand = () => {
    const last = policy.bands[policy.bands.length - 1];
    set({ bands: [...policy.bands, { from: (last?.from ?? 0) + 50_000, rate: last?.rate ?? 0 }] });
  };
  const removeBand = (i: number) => set({ bands: policy.bands.filter((_, j) => j !== i) });

  return (
    <div>
      {/* Version + effective year */}
      <div style={sectionStyle}>
        {!hideVersion && (
          <label style={{ display: 'inline-block', marginRight: 16 }}>
            <span style={labelStyle}>{versionLabel}</span>
            <input
              className="input"
              value={policy.policy_version}
              placeholder="e.g. 2026-v1"
              style={{ width: 200 }}
              onChange={(e) => set({ policy_version: e.target.value })}
            />
          </label>
        )}
        <NumField
          label="Invoice Year"
          value={policy.effective_assessment_year ?? ''}
          onChange={(v) => set({ effective_assessment_year: v })}
          width={90}
        />
        <NullableNumField
          label="Basis Year"
          value={policy.smoothing.basis_year ?? null}
          onChange={(v) => set({ smoothing: { ...policy.smoothing, basis_year: v } })}
          width={90}
        />
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
          Invoice Year = the year the quota is charged. Basis Year = the year(s) it's calculated on
          (income).
        </div>
        {versionHint && (
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>{versionHint}</div>
        )}
      </div>

      {/* Exclusions: flat base + which categories count */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Exclusions</div>
        <NumField
          label="Base exclusion ($, off every church)"
          value={policy.base_exclusion ?? 0}
          onChange={(v) => set({ base_exclusion: v })}
          width={130}
        />
        <div style={{ marginTop: 10 }}>
          <span style={labelStyle}>
            % of each contribution to exclude (amounts entered per church on the Contributions tab).
            0 = not excluded, 100 = fully excluded.
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 6 }}>
            {categories.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>No categories defined.</span>
            )}
            {categories.map((cat) => (
              <label key={cat.category_key} style={{ display: 'inline-block' }}>
                <span style={labelStyle}>{cat.label}</span>
                <div className="pct-input">
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round((exclPct[cat.category_key] ?? 0) * 100)}
                    style={{ width: 84, textAlign: 'right' }}
                    onChange={(e) => setCategoryPct(cat.category_key, Number(e.target.value) || 0)}
                  />
                  <span className="pct-suffix">%</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Smoothing */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Smoothing</div>
        <NumField
          label="Years to average"
          value={policy.smoothing.years}
          onChange={(v) => set({ smoothing: { ...policy.smoothing, years: v } })}
          width={90}
        />
        <NumField
          label="Min years required"
          value={policy.smoothing.min_years_required}
          onChange={(v) => set({ smoothing: { ...policy.smoothing, min_years_required: v } })}
          width={90}
        />
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
          Years to average is the number of years (ending at the Basis Year, above) to include in a
          rolling average — set to 0 if a rolling average is not required.
        </div>
      </div>

      {/* Bands */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
          Progressive bands (marginal)
        </div>
        <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--text2)' }}>
              <th style={{ textAlign: 'left', padding: '2px 12px 2px 0' }}>From (income ≥)</th>
              <th style={{ textAlign: 'left', padding: '2px 12px 2px 0' }}>Rate %</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {policy.bands.map((b, i) => (
              <tr key={i}>
                <td style={{ padding: '2px 12px 2px 0' }}>
                  <input
                    className="input"
                    type="number"
                    value={b.from}
                    style={{ width: 130, textAlign: 'right' }}
                    onChange={(e) => setBand(i, { from: num(e.target.value) })}
                  />
                </td>
                <td style={{ padding: '2px 12px 2px 0' }}>
                  <PctInput value={b.rate} onChange={(v) => setBand(i, { rate: v ?? 0 })} />
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => removeBand(i)}
                    disabled={policy.bands.length <= 1}
                    title="Remove band"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-sm btn-ghost" onClick={addBand} style={{ marginTop: 6 }}>
          <Plus size={13} />
          &nbsp;Add band
        </button>
      </div>

      {/* Phase-in */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Phase-in</div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 20 }}>
          <input
            type="checkbox"
            checked={policy.phase_in.enabled}
            onChange={(e) => set({ phase_in: { ...policy.phase_in, enabled: e.target.checked } })}
          />
          <span style={{ fontSize: 12 }}>Enabled</span>
        </label>
        <label style={{ display: 'inline-block', marginRight: 16 }}>
          <span style={labelStyle}>
            Ramp factors from the Invoice Year (comma-separated, e.g. 0.5, 0.75, 1.0)
          </span>
          <input
            className="input"
            value={policy.phase_in.schedule.join(', ')}
            style={{ width: 260 }}
            disabled={!policy.phase_in.enabled}
            onChange={(e) =>
              set({
                phase_in: {
                  ...policy.phase_in,
                  schedule: e.target.value
                    .split(',')
                    .map((s) => Number(s.trim()))
                    .filter((n) => Number.isFinite(n)),
                },
              })
            }
          />
        </label>
      </div>

      {/* Floor, caps, rounding */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Floor & caps</div>
        <NumField
          label="Floor (min quota $)"
          value={policy.floor}
          onChange={(v) => set({ floor: v })}
        />
        <NullableNumField
          label="Cap amount ($)"
          value={policy.cap.cap_amount}
          onChange={(v) => set({ cap: { ...policy.cap, cap_amount: v } })}
        />
        <label
          htmlFor="policy-cap-pct-of-assessable"
          style={{ display: 'inline-block', marginRight: 16, verticalAlign: 'top' }}
        >
          <span style={labelStyle}>Cap % of assessable</span>
          <PctInput
            id="policy-cap-pct-of-assessable"
            value={policy.cap.cap_pct_of_assessable}
            allowNull
            width={110}
            onChange={(v) => set({ cap: { ...policy.cap, cap_pct_of_assessable: v } })}
          />
        </label>
        <label
          htmlFor="policy-yoy-increase-cap"
          style={{ display: 'inline-block', marginRight: 16, verticalAlign: 'top' }}
        >
          <span style={labelStyle}>Year-on-year increase cap %</span>
          <PctInput
            id="policy-yoy-increase-cap"
            value={policy.yoy_increase_cap}
            allowNull
            width={130}
            onChange={(v) => set({ yoy_increase_cap: v })}
          />
        </label>
        <div
          style={{ display: 'inline-block', marginRight: 16, verticalAlign: 'top' }}
          title="When on, a church's quota is never set below its current (last-year) quota — if the calc comes out lower, it's held at last year's figure."
        >
          <span style={labelStyle}>Collar</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 30 }}>
            <Switch
              checked={policy.collar ?? false}
              onChange={(v) => set({ collar: v })}
              label="Collar"
            />
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>
              {policy.collar ? 'On' : 'Off'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
